import { z } from 'zod';
import { isCompleteUsPhone, toDigits } from '@/lib/phone/usPhone';
import type { ConsentFormValues } from './formValues';

export const MOBILE_PHONE_ERROR = 'Mobile number required to consent';
export const INCOMPLETE_MOBILE_PHONE_ERROR = 'Enter a valid 10-digit mobile number.';
export const INVALID_MOBILE_PHONE_ERROR = 'Enter a valid US mobile number.';

const mobilePhoneError = (value: string): string | null => {
  const digits = toDigits(value);
  if (digits.length === 0) return MOBILE_PHONE_ERROR;
  if (digits.length < 10) return INCOMPLETE_MOBILE_PHONE_ERROR;
  return isCompleteUsPhone(digits) ? null : INVALID_MOBILE_PHONE_ERROR;
};

export const ConsentFormSchema: z.ZodType<ConsentFormValues> = z
  .object({
    electronicDelivery: z.boolean(),
    textAlerts: z.boolean(),
    automatedCalling: z.boolean(),
    mobilePhone: z.string(),
  })
  .superRefine((values, ctx) => {
    const needsPhone = values.textAlerts || values.automatedCalling;
    if (!needsPhone) return;

    const message = mobilePhoneError(values.mobilePhone);
    if (message) ctx.addIssue({ code: 'custom', path: ['mobilePhone'], message });
  });

// Falls back to MOBILE_PHONE_ERROR so any invalid form still blocks submit —
// the phone refinement is currently the only way parsing can fail.
export const findMobilePhoneError = (values: ConsentFormValues): string | null => {
  const result = ConsentFormSchema.safeParse(values);
  if (result.success) return null;
  return (
    result.error.issues.find((issue) => issue.path[0] === 'mobilePhone')?.message ??
    MOBILE_PHONE_ERROR
  );
};
